import { twMerge } from "tailwind-merge";
import type { ReactNode } from "react";
import type { Message } from "@langchain/langgraph-sdk";
import type { MessageContent } from "../attachments/types";
import { extractText, extractImageUrls, extractDocuments } from "../attachments/types";
import { MessageImages } from "../attachments/MessageImages";
import { MessageDocuments } from "../attachments/MessageDocuments";

/**
 * Props for UserMessage component.
 *
 * Preferred usage (pass full message object):
 *   <Chat.UserMessage message={msg} />
 *
 * Simple usage:
 *   <Chat.UserMessage>Hello world</Chat.UserMessage>
 *
 * Content-based (handles text + images from LangGraph MessageContent):
 *   <Chat.UserMessage content={message.content} />
 */
export interface UserMessageProps {
  /** Full LangGraph message — extracts content and additional_kwargs automatically */
  message?: Message;
  /** Simple text content (mutually exclusive with content) */
  children?: ReactNode;
  /** LangGraph MessageContent — string or array of content parts */
  content?: MessageContent;
  /** LangGraph additional_kwargs — used to look up document filenames */
  additional_kwargs?: Record<string, unknown>;
  className?: string;
}

export type UserMessageSimpleProps = Omit<UserMessageProps, "content" | "message"> & { children: ReactNode };
export type UserMessageContentProps = Omit<UserMessageProps, "children" | "message"> & { content: MessageContent };

export function UserMessage(props: UserMessageProps) {
  const { className, message } = props;

  // Resolve content and additional_kwargs from message prop or direct props
  const resolvedContent = message?.content ?? props.content;
  const resolvedKwargs = message?.additional_kwargs ?? props.additional_kwargs;

  // The user side stays a right-aligned bubble (the assistant side is a
  // MessageLockup) — the asymmetry makes the conversation easy to scan.
  if (resolvedContent !== undefined) {
    const textContent = extractText(resolvedContent);
    const imageUrls = extractImageUrls(resolvedContent);
    const documents = extractDocuments(resolvedContent, resolvedKwargs);

    return (
      <div data-role="user" className="flex flex-col items-end">
        {textContent && (
          <div
            data-testid="user-message"
            data-role="user"
            className={twMerge(
              "bg-base-100 border border-neutral-200 rounded-2xl rounded-br-md px-4 py-3 max-w-[80%] ml-auto",
              className
            )}
          >
            <p className="whitespace-pre-wrap break-words text-sm text-neutral-800 [overflow-wrap:anywhere]">{textContent}</p>
          </div>
        )}
        {imageUrls.length > 0 && <MessageImages urls={imageUrls} />}
        {documents.length > 0 && <MessageDocuments files={documents} />}
      </div>
    );
  }

  // Simple children-based rendering
  return (
    <div
      data-testid="user-message"
      data-role="user"
      className={twMerge(
        "bg-base-100 border border-neutral-200 rounded-2xl rounded-br-md px-4 py-3 max-w-[80%] ml-auto",
        className
      )}
    >
      <p className="whitespace-pre-wrap break-words text-sm text-neutral-800 [overflow-wrap:anywhere]">{props.children}</p>
    </div>
  );
}

UserMessage.Images = MessageImages;
UserMessage.Documents = MessageDocuments;
